/**
 * Dify API 서비스
 * 프로젝트 스케줄 생성을 위한 AI 연동
 */

import i18n from '../i18n';

// Dify API 설정
const DIFY_CONFIG = {
  baseUrl: 'https://api.dify.ai/v1',
  apiKey: 'app-zEDy9GwrrmXG72iEf75luMvA',
};

/**
 * AI 생성 중 표시할 로딩 메시지 목록
 * 언어 전환에 따르도록 호출 시점에 t()를 평가하는 함수로 제공한다.
 */
const getLoadingMessages = () => [
  { text: i18n.t('dify:loading.analyze'), icon: '🔍' },
  { text: i18n.t('dify:loading.wbs'), icon: '📊' },
  { text: i18n.t('dify:loading.phases'), icon: '🏗️' },
  { text: i18n.t('dify:loading.tasks'), icon: '📋' },
  { text: i18n.t('dify:loading.schedule'), icon: '📅' },
  { text: i18n.t('dify:loading.checklist'), icon: '✅' },
  { text: i18n.t('dify:loading.risk'), icon: '⚠️' },
  { text: i18n.t('dify:loading.optimize'), icon: '⚡' },
  { text: i18n.t('dify:loading.deliverables'), icon: '📝' },
  { text: i18n.t('dify:loading.review'), icon: '🎯' },
];

/**
 * 로딩 메시지 순환 관리자
 */
const createLoadingMessageManager = (onMessage) => {
  let currentIndex = 0;
  let intervalId = null;

  const start = () => {
    // 생성 시작 시점의 언어로 메시지를 확정
    const messages = getLoadingMessages();
    if (onMessage) {
      onMessage(messages[0]);
    }
    intervalId = setInterval(() => {
      currentIndex = (currentIndex + 1) % messages.length;
      if (onMessage) {
        onMessage(messages[currentIndex]);
      }
    }, 14000);
  };

  const stop = () => {
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
  };

  return { start, stop };
};

/**
 * JSON 응답 파싱 (JSON Schema 사용 시 순수 JSON, 미사용 시 코드블록 추출)
 * @param {string} text - LLM 응답 텍스트
 * @returns {Object} 파싱된 JSON 객체
 */
const parseJsonResponse = (text) => {
  if (!text || typeof text !== 'string') {
    throw new Error('응답이 비어있습니다.');
  }

  let jsonStr = text.trim();

  // 1. 순수 JSON인 경우 바로 파싱 시도
  if (jsonStr.startsWith('{') || jsonStr.startsWith('[')) {
    try {
      return JSON.parse(jsonStr);
    } catch (e) {
      // 파싱 실패 시 다음 단계로
    }
  }

  // 2. 코드블록에서 JSON 추출 (```json ... ``` 또는 ``` ... ```)
  const codeBlockMatch = jsonStr.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  if (codeBlockMatch) {
    jsonStr = codeBlockMatch[1].trim();
  }

  // 3. JSON 객체 패턴 찾기 (최후의 수단)
  if (!jsonStr.startsWith('{') && !jsonStr.startsWith('[')) {
    const jsonObjMatch = jsonStr.match(/(\{[\s\S]*\})/);
    if (jsonObjMatch) {
      jsonStr = jsonObjMatch[1];
    }
  }

  // 4. 흔한 JSON 오류 수정
  jsonStr = jsonStr
    .replace(/,\s*}/g, '}') // trailing comma in object
    .replace(/,\s*]/g, ']') // trailing comma in array
    .trim();

  // 5. 제어 문자 이스케이프 함수
  const escapeControlChars = (str) => {
    let result = '';
    let inString = false;
    let escapeNext = false;

    for (let i = 0; i < str.length; i++) {
      const char = str[i];
      const charCode = str.charCodeAt(i);

      if (escapeNext) {
        result += char;
        escapeNext = false;
        continue;
      }

      if (char === '\\' && inString) {
        result += char;
        escapeNext = true;
        continue;
      }

      if (char === '"') {
        inString = !inString;
        result += char;
        continue;
      }

      // 문자열 내부의 제어 문자 이스케이프
      if (inString && charCode < 32) {
        if (charCode === 10)
          result += '\\n'; // LF
        else if (charCode === 13)
          result += '\\r'; // CR
        else if (charCode === 9)
          result += '\\t'; // Tab
        else if (charCode === 8)
          result += '\\b'; // Backspace
        else if (charCode === 12)
          result += '\\f'; // Form feed
        else result += `\\u${charCode.toString(16).padStart(4, '0')}`; // 기타 제어 문자
        continue;
      }

      result += char;
    }

    return result;
  };

  // 6. 첫 번째 파싱 시도
  try {
    return JSON.parse(jsonStr);
  } catch (e) {
    // 7. 제어 문자 이스케이프 후 재시도
    try {
      const escapedJson = escapeControlChars(jsonStr);
      return JSON.parse(escapedJson);
    } catch (e2) {
      // 8. 불완전한 JSON 잘라내기 시도
      try {
        const escapedJson = escapeControlChars(jsonStr);

        // 마지막 유효한 닫는 괄호까지 찾기
        let lastValidIdx = escapedJson.length;
        let braceCount = 0;
        let bracketCount = 0;
        let inString = false;
        let escapeNext = false;

        for (let i = 0; i < escapedJson.length; i++) {
          const char = escapedJson[i];

          if (escapeNext) {
            escapeNext = false;
            continue;
          }

          if (char === '\\') {
            escapeNext = true;
            continue;
          }

          if (char === '"') {
            inString = !inString;
            continue;
          }

          if (!inString) {
            if (char === '{') braceCount++;
            if (char === '}') {
              braceCount--;
              if (braceCount === 0 && bracketCount === 0) {
                lastValidIdx = i + 1;
                break;
              }
            }
            if (char === '[') bracketCount++;
            if (char === ']') bracketCount--;
          }
        }

        const truncatedJson = escapedJson.substring(0, lastValidIdx);
        return JSON.parse(truncatedJson);
      } catch (e3) {
        console.error('[parseJsonResponse] 파싱 실패:', e.message);
        throw new Error(`JSON 파싱 실패: ${e.message}`);
      }
    }
  }
};

/**
 * 사용자 프롬프트 생성 함수
 */
const generateUserPrompt = (projectInfo) => {
  const { name, description, startDate, endDate, totalDays, totalWeeks } = projectInfo;

  return `# 프로젝트 일정 생성 요청

## 프로젝트 기본 정보
| 항목 | 내용 |
|------|------|
| 프로젝트명 | ${name} |
| 시작일 | ${startDate} |
| 종료일 | ${endDate} |
| 총 기간 | ${totalDays}일 (약 ${totalWeeks}주) |

## 프로젝트 개요
${description}

## ⚠️ 1단계: 프로젝트 유형 판단 (필수)

프로젝트 설명을 분석하여 아래 3가지 유형 중 하나로 판단하세요:

### 유형 A: 개인 실행형
**특징**: 본인이 직접 활동을 수행
**예시**: 다이어트, 운동, 독서, 자격증 공부, 습관 형성, 챌린지

**일정 생성 방식**:
- ❌ 금지: "운동 계획 수립", "식단 설계", "목표 분석" (메타 레벨)
- ✅ 필수: "유산소 운동 주 3회", "근력 운동", "식단 실천" (실행 레벨)
- 준비 단계: 5-10%
- 실행 단계: 85-90%
- 체크리스트: 구체적 행동 + 수치 (횟수, 시간, 양)

### 유형 B: 기획/개발형
**특징**: 결과물(제품, 서비스, 시스템)을 만드는 프로젝트
**예시**: 앱 개발, 웹사이트 구축, 신제품 개발, 서비스 런칭

**일정 생성 방식**:
- 기획 → 설계 → 개발/제작 → 테스트 → 배포
- 준비/기획: 15-20%
- 실행/개발: 60-70%
- 검증/마무리: 15-20%
- 체크리스트: 산출물 중심

### 유형 C: 이벤트/행사형
**특징**: 특정 날짜에 실행되는 일회성 이벤트
**예시**: 결혼식, 세미나, 워크숍, 전시회

**일정 생성 방식**:
- 준비: 70-80% (섭외, 예약, 제작)
- 실행: 10-15% (D-day)
- 마무리: 10%

## 2단계: 일정 배분 원칙

**기간은 작업 난이도에 따라 유동적으로:**
- 단순 작업: 1-3일
- 일반 작업: 3-7일
- 복잡한 작업: 1-3주
- **모든 Task를 동일 기간(예: 1주)으로 설정 금지!**

## 3단계: Category/Task/Checklist 구성

### Category
- 프로젝트 규모와 복잡도에 맞게 유동적으로 구성
- 핵심 활동 영역별로 구성
- 중요도가 높은 영역은 세분화 가능

### Task
- 핵심 작업 중심, 중요도에 따라 개수 조절
- 우선순위가 높거나 복잡한 카테고리는 더 많은 태스크 가능
- 선후행 관계 고려

### Checklist
- 즉시 실행 가능한 구체적 행동
- 완료 여부 명확히 판단 가능
- 중요한 태스크는 더 상세한 체크리스트 작성 가능
- 각 항목은 반드시 { "name": "항목명" } 객체 형태로 작성

## 비용 산정 (한국 시장 2024-2025 실제 시세)

### 기획/개발형 - 프리랜서 월 단가
- 주니어 개발자 (4년 이하): 400-450만원
- 미드레벨 개발자 (4-8년): 530-600만원
- 시니어 개발자 (8-12년): 700-800만원
- 특급 개발자 (12년+): 800-1,000만원
- UI/UX 디자이너 (초중급): 350-450만원
- UI/UX 디자이너 (고급): 450-550만원
- 기획자/PM: 400-600만원

### 개인 실행형 - 실제 지출 비용
- PT (주 2회, 월 8회): 40-60만원/월
- PT (주 3회, 월 12회): 60-85만원/월
- 헬스장 이용료: 8-15만원/월
- 식단/보조제: 20-40만원/월

### 산정 방법
- 기획/개발형: (투입 인력 × 기간 × 월 단가) + 버퍼 10-15%
- 개인 실행형: 실제 서비스/장비 비용 합산

## 출력 시 주의사항
- **project_type**: 위 1단계에서 판단한 유형 (개인실행형/기획개발형/이벤트행사형)
- **start_date, end_date**: YYYY-MM-DD 형식, 반드시 프로젝트 기간(${startDate} ~ ${endDate}) 내로 설정
- **sort_order**: 카테고리 순서 (1부터 시작)
- **ai_estimated_cost**: 숫자만 (원 단위, 따옴표 없이)
- **ai_estimated_cost_reasons**: 간결한 비용 산정 근거
- **checklists**: 각 항목은 반드시 { "name": "항목명" } 객체 형태로 작성 (hint는 포함하지 마세요)

## 출력 JSON 구조 (반드시 아래 구조를 따를 것)

{
  "project_type": "개인실행형",
  "categories": [
    {
      "name": "카테고리명",
      "sort_order": 1,
      "tasks": [
        {
          "name": "작업명",
          "description": "작업의 구체적인 목표와 방법 설명",
          "start_date": "YYYY-MM-DD",
          "end_date": "YYYY-MM-DD",
          "checklists": [
            { "name": "즉시 실행 가능한 구체적 행동 항목" },
            { "name": "완료 여부를 명확히 판단할 수 있는 항목" }
          ]
        }
      ]
    }
  ],
  "ai_estimated_cost": 5000000,
  "ai_estimated_cost_reasons": "비용 산정 근거"
}

⚠️ 중요: checklists 배열의 각 항목은 반드시 "name" 키를 가진 객체여야 합니다.
문자열 배열이나 다른 키(title, text, description, content, item 등)는 절대 사용하지 마세요.`;
};

/**
 * Dify 스트리밍 중지 API 호출
 * @param {string} taskId - 중지할 task ID
 * @param {string} user - 사용자 ID
 */
const stopChatMessage = async (taskId, user) => {
  if (!taskId) return;

  try {
    await fetch(`${DIFY_CONFIG.baseUrl}/chat-messages/${taskId}/stop`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${DIFY_CONFIG.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user }),
    });
  } catch (e) {
    // 중지 실패는 무시 (이미 완료되었거나 네트워크 오류)
  }
};

/**
 * Dify Chat API 호출 (Streaming 모드 - Agent 앱 지원)
 * @param {Object} options - 옵션
 * @param {AbortSignal} options.signal - 취소 시그널 (optional)
 * @param {Function} options.onTaskId - task_id 수신 콜백 (optional)
 */
const sendChatMessage = async ({
  query,
  user,
  inputs = {},
  conversationId = '',
  signal,
  onTaskId,
}) => {
  const response = await fetch(`${DIFY_CONFIG.baseUrl}/chat-messages`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${DIFY_CONFIG.apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      inputs,
      query,
      response_mode: 'streaming',
      conversation_id: conversationId,
      user,
    }),
    signal,
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Dify API 오류: ${response.status} - ${errorText}`);
  }

  // SSE 스트림 파싱
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let fullAnswer = '';
  let messageId = '';
  let taskId = '';
  let conversationIdResult = '';
  let buffer = ''; // 불완전한 청크를 버퍼링

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;

      // 완전한 라인 단위로 처리
      const lines = buffer.split('\n');
      // 마지막 줄은 불완전할 수 있으므로 버퍼에 보관
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (trimmedLine.startsWith('data: ')) {
          const data = trimmedLine.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);

            // task_id 캡처 (첫 이벤트에서 수신)
            if (parsed.task_id && !taskId) {
              taskId = parsed.task_id;
              if (onTaskId) onTaskId(taskId);
            }

            // agent_message 또는 message 이벤트에서 답변 추출
            if (parsed.event === 'agent_message' || parsed.event === 'message') {
              fullAnswer += parsed.answer || '';
              messageId = parsed.message_id || messageId;
              conversationIdResult = parsed.conversation_id || conversationIdResult;
            }

            // message_end 이벤트
            if (parsed.event === 'message_end') {
              messageId = parsed.message_id || messageId;
              conversationIdResult = parsed.conversation_id || conversationIdResult;
            }
          } catch (e) {
            // JSON 파싱 실패는 무시 (불완전한 청크)
          }
        }
      }
    }

    // 버퍼에 남은 데이터 처리
    if (buffer.trim().startsWith('data: ')) {
      const data = buffer.trim().slice(6);
      if (data !== '[DONE]') {
        try {
          const parsed = JSON.parse(data);
          if (parsed.event === 'agent_message' || parsed.event === 'message') {
            fullAnswer += parsed.answer || '';
          }
        } catch (e) {
          // 무시
        }
      }
    }
  } finally {
    reader.releaseLock();
  }

  return {
    answer: fullAnswer,
    message_id: messageId,
    task_id: taskId,
    conversation_id: conversationIdResult,
  };
};

/**
 * Dify Chat API 호출 (Blocking 모드 - 스트리밍 실패 시 폴백)
 */
const sendBlockingMessage = async ({ query, user, inputs = {} }) => {
  const response = await fetch(`${DIFY_CONFIG.baseUrl}/chat-messages`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${DIFY_CONFIG.apiKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      inputs,
      query,
      response_mode: 'blocking',
      conversation_id: '',
      user,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Dify API 오류 (blocking): ${response.status} - ${errorText}`);
  }

  const data = await response.json();
  return {
    answer: data.answer || '',
    message_id: data.message_id || '',
    conversation_id: data.conversation_id || '',
  };
};

/**
 * 프로젝트 스케줄 생성 요청
 * @param {Object} projectInfo - 프로젝트 정보
 * @param {string} userId - 사용자 ID
 * @param {Function} onProgress - 진행 상황 콜백 ({ text, icon } 객체 전달)
 * @param {Object} options - 추가 옵션
 * @param {AbortSignal} options.signal - 취소 시그널 (optional)
 * @param {Function} options.onTaskId - task_id 수신 콜백 (optional)
 * @returns {Promise<Object>} 생성된 프로젝트 스케줄 (Supabase 저장용 구조)
 */
export const generateProjectSchedule = async (projectInfo, userId, onProgress, options = {}) => {
  const { signal, onTaskId } = options;
  const { name, description, startDate, endDate } = projectInfo;

  // 프로젝트 기간 계산
  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  const totalWeeks = Math.ceil(totalDays / 7);

  // 사용자 프롬프트 생성
  const userPrompt = generateUserPrompt({
    name,
    description,
    startDate,
    endDate,
    totalDays,
    totalWeeks,
  });

  // 로딩 메시지 관리자 생성 및 시작
  const loadingManager = createLoadingMessageManager(onProgress);
  loadingManager.start();

  try {
    const result = await sendChatMessage({
      query: userPrompt,
      user: userId,
      inputs: {
        project_name: name,
        project_description: description,
        start_date: startDate,
        end_date: endDate,
      },
      signal,
      onTaskId,
    });

    loadingManager.stop();

    if (!result || !result.answer) {
      throw new Error('Dify API 응답이 비어있습니다.');
    }

    // JSON 파싱 (JSON Schema 사용 시 순수 JSON 반환)
    let parsedData;
    try {
      parsedData = parseJsonResponse(result.answer);
    } catch (parseError) {
      // 스트리밍 파싱 실패 시 blocking 모드로 재시도
      if (onProgress) onProgress({ text: i18n.t('dify:retry'), icon: '🔄' });

      const retryResult = await sendBlockingMessage({
        query: userPrompt,
        user: userId,
        inputs: {
          project_name: name,
          project_description: description,
          start_date: startDate,
          end_date: endDate,
        },
      });

      if (!retryResult || !retryResult.answer) {
        throw new Error('Dify API 재시도 응답이 비어있습니다.');
      }

      parsedData = parseJsonResponse(retryResult.answer);
    }

    // Supabase 저장용 구조로 변환
    return transformToSupabaseStructure(parsedData, projectInfo);
  } catch (error) {
    loadingManager.stop();
    // 사용자 취소는 에러 로그 출력하지 않음
    if (error.name !== 'AbortError') {
      console.error('[Dify] 오류 발생:', error);
    }
    throw error;
  }
};

/**
 * Dify 응답을 Supabase 저장용 구조로 변환
 */
const transformToSupabaseStructure = (difyResponse, projectInfo) => {
  const { name, description, startDate, endDate } = projectInfo;

  if (!difyResponse || !difyResponse.categories) {
    throw new Error('difyResponse.categories가 정의되지 않았습니다.');
  }

  const categories = difyResponse.categories
    .map((category, categoryIndex) => {
      if (!category) return null;

      return {
        name: category.name || `카테고리 ${categoryIndex + 1}`,
        sort_order: category.sort_order || categoryIndex + 1,
        tasks: (category.tasks || [])
          .map((task) => {
            if (!task) return null;

            return {
              name: task.name || '작업',
              description: task.description || '',
              start_date: task.start_date || startDate,
              end_date: task.end_date || endDate,
              checklists: (task.checklists || []).map((checklist) => {
                // 문자열인 경우 직접 name으로 사용
                if (typeof checklist === 'string') {
                  return { name: checklist, hint: null, is_completed: false };
                }
                // 객체인 경우 name 키 우선, 다양한 키 탐색
                const checklistName =
                  checklist?.name ||
                  checklist?.title ||
                  checklist?.text ||
                  checklist?.description ||
                  checklist?.content ||
                  checklist?.item ||
                  checklist?.label;
                // 알려진 키가 없으면 객체의 첫 번째 문자열 값 사용
                const resolvedName =
                  checklistName ||
                  Object.values(checklist || {}).find((v) => typeof v === 'string' && v.length > 0);
                if (!resolvedName) {
                  console.warn(
                    '[transformToSupabaseStructure] 체크리스트 이름 추출 실패:',
                    JSON.stringify(checklist)
                  );
                }
                return {
                  name: resolvedName || '체크리스트 항목',
                  hint: null, // hint는 별도 Dify 오케스트레이션으로 생성
                  is_completed: false,
                };
              }),
            };
          })
          .filter(Boolean),
      };
    })
    .filter(Boolean);

  return {
    project: {
      name,
      description,
      start_date: startDate,
      end_date: endDate,
      ai_estimated_cost: difyResponse.ai_estimated_cost || null,
      ai_estimated_cost_reasons: difyResponse.ai_estimated_cost_reasons || null,
    },
    categories,
  };
};

// ============================================================================
// 프로젝트 입력값 검증 API
// 프로젝트 생성 전 기간 및 개요 적절성 검증
// ============================================================================

/**
 * 프로젝트 입력값 검증
 * @param {Object} projectInfo - 프로젝트 정보 { name, description, startDate, endDate }
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Object>} { isValid: boolean, warnings: string[], suggestions: string }
 */
export const validateProjectInput = async (projectInfo, userId) => {
  const { name, description, startDate, endDate } = projectInfo;

  // 기간 계산
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(startDate);
  const end = new Date(endDate);
  const totalDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  const totalWeeks = Math.ceil(totalDays / 7);
  const daysFromToday = Math.ceil((start - today) / (1000 * 60 * 60 * 24));
  const daysUntilEnd = Math.ceil((end - today) / (1000 * 60 * 60 * 24));

  const prompt = `# 프로젝트 입력값 검증 요청

## 오늘 날짜
${today.toLocaleDateString('ko-KR')} (${today.toISOString().split('T')[0]})

## 입력된 프로젝트 정보
| 항목 | 내용 |
|------|------|
| 프로젝트명 | ${name} |
| 시작일 | ${startDate} (오늘 기준 ${daysFromToday > 0 ? `${daysFromToday}일 후` : daysFromToday === 0 ? '오늘' : `${Math.abs(daysFromToday)}일 전`}) |
| 종료일 | ${endDate} (오늘 기준 ${daysUntilEnd > 0 ? `${daysUntilEnd}일 후` : `${Math.abs(daysUntilEnd)}일 전`}) |
| 총 기간 | ${totalDays}일 (약 ${totalWeeks}주) |

## 프로젝트 개요
${description}

## 검증 항목

### 1. 날짜 적절성 검증
- **과거 시작일**: 시작일이 오늘보다 과거인가? (경고 필요)
- **과거 종료일**: 종료일이 오늘보다 과거인가? (심각한 경고)
- **너무 먼 미래**: 시작일이 1년 이상 미래인가? (경고)
- **비현실적 종료일**: 종료일이 5년 이상 미래인가? (경고)

### 2. 기간-내용 적절성 검증
프로젝트 개요를 분석하여:
- **너무 짧은 기간**: 설명된 작업량 대비 기간이 현실적으로 불가능해 보이는가?
- **너무 긴 기간**: 간단한 작업에 비해 기간이 과도하게 긴가?
- **애매한 개요**: 프로젝트 개요가 너무 모호하여 일정 생성이 어려운가?

### 3. 판단 기준
- 경고가 필요한 경우에만 warnings에 추가
- 사용자가 의도적으로 선택했을 수 있으므로 부드러운 톤으로 작성
- 심각한 문제(과거 종료일)는 명확히 지적

## 출력 형식 (JSON만 출력)
\`\`\`json
{
  "isValid": true 또는 false,
  "warnings": ["경고 메시지 1", "경고 메시지 2"],
  "suggestions": "사용자에게 전달할 종합 의견 (문제가 없으면 빈 문자열)"
}
\`\`\`

### 예시 응답 1 (문제 없음)
\`\`\`json
{
  "isValid": true,
  "warnings": [],
  "suggestions": ""
}
\`\`\`

### 예시 응답 2 (경고 있음)
\`\`\`json
{
  "isValid": true,
  "warnings": ["시작일이 오늘보다 2주 전입니다. 이미 진행 중인 프로젝트인가요?"],
  "suggestions": "시작일이 과거로 설정되어 있습니다. 이미 진행 중인 프로젝트라면 현재까지의 진행 상황을 반영하기 어려울 수 있어요. 시작일을 오늘로 변경하시겠어요?"
}
\`\`\`

### 예시 응답 3 (심각한 문제)
\`\`\`json
{
  "isValid": false,
  "warnings": ["종료일이 이미 지났습니다."],
  "suggestions": "종료일이 오늘(${today.toISOString().split('T')[0]})보다 과거입니다. 프로젝트 종료일을 수정해 주세요."
}
\`\`\``;

  try {
    const response = await fetch(`${DIFY_VALIDATOR_CONFIG.baseUrl}/chat-messages`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${DIFY_VALIDATOR_CONFIG.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        inputs: {},
        query: prompt,
        response_mode: 'streaming',
        conversation_id: '',
        user: userId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Dify API 오류: ${response.status} - ${errorText}`);
    }

    // SSE 스트림 파싱
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullAnswer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.event === 'agent_message' || parsed.event === 'message') {
              fullAnswer += parsed.answer || '';
            }
          } catch (e) {
            // JSON 파싱 실패는 무시
          }
        }
      }
    }

    // JSON 파싱 (여러 방법 시도)
    let result = null;
    let jsonString = fullAnswer.trim();

    // 1. 코드블록에서 JSON 추출
    const jsonMatch = jsonString.match(/```json\s*([\s\S]*?)\s*```/);
    if (jsonMatch) {
      jsonString = jsonMatch[1].trim();
    }

    // 2. JSON 객체 패턴 찾기
    if (!jsonString.startsWith('{')) {
      const jsonObjMatch = jsonString.match(/(\{[\s\S]*\})/);
      if (jsonObjMatch) {
        jsonString = jsonObjMatch[1];
      }
    }

    // 3. 흔한 JSON 오류 수정 및 파싱
    jsonString = jsonString.replace(/,\s*}/g, '}').replace(/,\s*]/g, ']').trim();

    try {
      result = JSON.parse(jsonString);
    } catch (e) {
      // 4. 첫 번째 유효한 JSON 객체만 추출 시도
      let braceCount = 0;
      let startIdx = jsonString.indexOf('{');
      if (startIdx !== -1) {
        for (let i = startIdx; i < jsonString.length; i++) {
          if (jsonString[i] === '{') braceCount++;
          if (jsonString[i] === '}') braceCount--;
          if (braceCount === 0) {
            const extracted = jsonString.substring(startIdx, i + 1);
            result = JSON.parse(extracted);
            break;
          }
        }
      }
    }

    if (!result) {
      throw new Error('JSON 파싱 실패');
    }

    return {
      isValid: result.isValid !== false,
      warnings: result.warnings || [],
      suggestions: result.suggestions || '',
    };
  } catch (error) {
    console.error('[Dify Validator] 검증 오류:', error);
    // 검증 실패 시 기본적으로 통과 처리 (AI 검증 없이 진행 가능)
    return {
      isValid: true,
      warnings: [],
      suggestions: '',
      error: error.message,
    };
  }
};

// ============================================================================
// AI 어시스턴트 API (질문 응답, 비용 재산출)
// 별도의 Dify 앱 사용 권장
// ============================================================================

// AI 어시스턴트 Dify 앱 설정 (별도 앱 생성 후 API Key 교체)
const DIFY_ASSISTANT_CONFIG = {
  baseUrl: 'https://api.dify.ai/v1',
  apiKey: 'app-9jqEh7px0jIi596sWwLVbe9T', // TODO: 새 Dify 앱의 API Key로 교체
};

// 프로젝트 입력값 검증 Dify 앱 설정
const DIFY_VALIDATOR_CONFIG = {
  baseUrl: 'https://api.dify.ai/v1',
  apiKey: 'app-e9uRIq9i2zhmeuTgDLVeS1V9', // TODO: 검증용 Dify 앱의 API Key로 교체
};

/**
 * 프로젝트 데이터를 AI 컨텍스트 문자열로 변환
 * @param {Object} project - 프로젝트 데이터
 * @returns {string} AI에게 전달할 프로젝트 컨텍스트
 */
export const serializeProjectContext = (project) => {
  if (!project) return '프로젝트 정보가 없습니다.';

  const today = new Date();
  const startDate = new Date(project.startDate);
  const endDate = new Date(project.endDate);
  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  const elapsedDays = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
  const remainingDays = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));
  const progressPercent = Math.min(100, Math.max(0, Math.round((elapsedDays / totalDays) * 100)));

  // 카테고리별 진행 상황 계산
  const categoryStats = (project.categories || []).map((cat) => {
    const tasks = cat.tasks || [];
    const totalChecklists = tasks.reduce((sum, task) => sum + (task.checklist?.length || 0), 0);
    const completedChecklists = tasks.reduce(
      (sum, task) => sum + (task.checklist?.filter((c) => c.completed)?.length || 0),
      0
    );
    const progress =
      totalChecklists > 0 ? Math.round((completedChecklists / totalChecklists) * 100) : 0;

    return {
      name: cat.name,
      taskCount: tasks.length,
      totalChecklists,
      completedChecklists,
      progress,
      tasks: tasks.map((task) => ({
        name: task.name,
        startDate: task.startDate,
        endDate: task.endDate,
        checklistTotal: task.checklist?.length || 0,
        checklistCompleted: task.checklist?.filter((c) => c.completed)?.length || 0,
        progress:
          task.checklist?.length > 0
            ? Math.round(
                (task.checklist.filter((c) => c.completed).length / task.checklist.length) * 100
              )
            : 0,
      })),
    };
  });

  // 전체 진행률 계산
  const totalChecklists = categoryStats.reduce((sum, cat) => sum + cat.totalChecklists, 0);
  const completedChecklists = categoryStats.reduce((sum, cat) => sum + cat.completedChecklists, 0);
  const overallProgress =
    totalChecklists > 0 ? Math.round((completedChecklists / totalChecklists) * 100) : 0;

  return `# 프로젝트 현황

## 기본 정보
- 프로젝트명: ${project.name}
- 설명: ${project.description || '없음'}
- 시작일: ${startDate.toLocaleDateString('ko-KR')}
- 종료일: ${endDate.toLocaleDateString('ko-KR')}
- 총 기간: ${totalDays}일
- 경과일: ${elapsedDays}일
- 남은 일수: ${remainingDays}일
- 일정 진행률: ${progressPercent}% (기간 기준)

## 전체 진행 상황
- 전체 체크리스트: ${totalChecklists}개
- 완료된 체크리스트: ${completedChecklists}개
- 실제 진행률: ${overallProgress}%
- 예상 비용: ${project.ai_estimated_cost ? `${project.ai_estimated_cost.toLocaleString()}원` : '미정'}

## 카테고리별 상세 현황
${categoryStats
  .map(
    (cat, idx) => `
### ${idx + 1}. ${cat.name}
- 작업 수: ${cat.taskCount}개
- 체크리스트: ${cat.completedChecklists}/${cat.totalChecklists}개 완료 (${cat.progress}%)
${cat.tasks.map((task) => `  - ${task.name}: ${task.checklistCompleted}/${task.checklistTotal}개 완료 (${task.progress}%)`).join('\n')}
`
  )
  .join('\n')}

## 오늘 날짜
${today.toLocaleDateString('ko-KR')} (${today.toLocaleDateString('ko-KR', { weekday: 'long' })})
`;
};

/**
 * AI 어시스턴트에게 질문하기 (Streaming)
 * @param {Object} project - 프로젝트 데이터
 * @param {string} question - 사용자 질문
 * @param {string} userId - 사용자 ID
 * @returns {Promise<string>} AI 응답
 */
export const askAiAssistant = async (project, question, userId) => {
  const projectContext = serializeProjectContext(project);

  const prompt = `${projectContext}

---

## 사용자 질문
${question}

위 프로젝트 현황을 바탕으로 질문에 답변해 주세요. 답변은 간결하고 실용적으로 작성하세요.`;

  try {
    const response = await fetch(`${DIFY_ASSISTANT_CONFIG.baseUrl}/chat-messages`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${DIFY_ASSISTANT_CONFIG.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        inputs: {},
        query: prompt,
        response_mode: 'streaming',
        conversation_id: '',
        user: userId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Dify API 오류: ${response.status} - ${errorText}`);
    }

    // SSE 스트림 파싱
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullAnswer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.event === 'agent_message' || parsed.event === 'message') {
              fullAnswer += parsed.answer || '';
            }
          } catch (e) {
            // JSON 파싱 실패는 무시
          }
        }
      }
    }

    return fullAnswer;
  } catch (error) {
    console.error('[Dify Assistant] 오류:', error);
    throw error;
  }
};

/**
 * AI 비용 재산출
 * @param {Object} project - 프로젝트 데이터
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Object>} { cost: number, reasons: string }
 */
export const recalculateCost = async (project, userId) => {
  const projectContext = serializeProjectContext(project);

  // 기존 비용 정보 구성
  const hasExistingCost = project.ai_estimated_cost;
  const existingCostSection = hasExistingCost
    ? `
### 이전 예상 비용
- 금액: ${parseInt(project.ai_estimated_cost).toLocaleString()} ${project.cost_unit || '원'}
- 근거: ${project.ai_estimated_cost_reasons || '(없음)'}`
    : '';

  const prompt = `${projectContext}
${existingCostSection}

---

## 비용 재산출 요청

위 프로젝트 현황을 바탕으로 예상 비용을 산출해 주세요.

### 비용 산정 기준 (한국 2024-2025 프리랜서 월 단가)
- 주니어 개발자 (4년 이하): 400-450만원
- 미드레벨 개발자 (4-8년): 530-600만원
- 시니어 개발자 (8-12년): 700-800만원
- UI/UX 디자이너: 350-550만원
- 기획자/PM: 400-600만원

### 산정 방식
1. 남은 작업량(체크리스트 미완료 항목) 기준으로 필요 인력과 기간 산정
2. 팀 구성은 프로젝트 규모에 맞게 현실적으로 가정
3. 일정 지연이나 리스크로 인한 추가 비용은 반영하지 않음
4. 순수하게 남은 작업을 완료하는 데 필요한 인건비만 계산

### 출력 형식 (JSON만 출력)
\`\`\`json
{
  "ai_estimated_cost": 예상비용(숫자, 원 단위),
  "ai_estimated_cost_reasons": "간결한 산출 근거 (팀 구성, 예상 기간, 계산식 포함)"
}
\`\`\``;

  try {
    const response = await fetch(`${DIFY_ASSISTANT_CONFIG.baseUrl}/chat-messages`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${DIFY_ASSISTANT_CONFIG.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        inputs: {},
        query: prompt,
        response_mode: 'streaming',
        conversation_id: '',
        user: userId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Dify API 오류: ${response.status} - ${errorText}`);
    }

    // SSE 스트림 파싱
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullAnswer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      const chunk = decoder.decode(value, { stream: true });
      const lines = chunk.split('\n');

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          if (data === '[DONE]') continue;

          try {
            const parsed = JSON.parse(data);
            if (parsed.event === 'agent_message' || parsed.event === 'message') {
              fullAnswer += parsed.answer || '';
            }
          } catch (e) {
            // JSON 파싱 실패는 무시
          }
        }
      }
    }

    // JSON 파싱
    const jsonMatch = fullAnswer.match(/```json\s*([\s\S]*?)\s*```/);
    const jsonString = jsonMatch ? jsonMatch[1] : fullAnswer;
    const result = JSON.parse(jsonString.replace(/,\s*}/g, '}').replace(/,\s*]/g, ']').trim());

    return {
      cost: result.ai_estimated_cost,
      reasons: result.ai_estimated_cost_reasons,
    };
  } catch (error) {
    console.error('[Dify Assistant] 비용 재산출 오류:', error);
    throw error;
  }
};

// ============================================================================
// 체크리스트 AI 가이드(hint) 생성 API
// 별도의 Dify 오케스트레이션 사용 (속도 최적화)
// ============================================================================

// AI 가이드 생성 Dify 앱 설정
const DIFY_HINT_CONFIG = {
  baseUrl: 'https://api.dify.ai/v1',
  apiKey: 'app-Bzdk36EZwIOgS1gbVuWjUC6Q', // TODO: AI 가이드 생성용 Dify 앱의 API Key로 교체
};

/**
 * 체크리스트 AI 가이드(hint) 생성
 * @param {Object} taskInfo - { taskName, taskDescription, categoryName, startDate, endDate, checklists: [{id, name}] }
 * @param {Object} projectInfo - { name, description, startDate, endDate }
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} [{ checklistId, hint }]
 */
export const generateChecklistHints = async (taskInfo, projectInfo, userId) => {
  const {
    taskName,
    taskDescription,
    categoryName,
    startDate: taskStart,
    endDate: taskEnd,
    checklists,
  } = taskInfo;
  const {
    name: projectName,
    description: projectDescription,
    startDate: projStart,
    endDate: projEnd,
  } = projectInfo;

  const checklistList = checklists.map((c, i) => `${i + 1}. ${c.name}`).join('\n');

  const prompt = `# AI 가이드 생성 요청

## 프로젝트 정보
- 프로젝트명: ${projectName}
- 프로젝트 설명: ${projectDescription || '없음'}
- 프로젝트 기간: ${projStart || '미정'} ~ ${projEnd || '미정'}

## 카테고리
- ${categoryName || '없음'}

## 작업(Task) 정보
- 작업명: ${taskName}
- 작업 설명: ${taskDescription || '없음'}
- 작업 기간: ${taskStart || '미정'} ~ ${taskEnd || '미정'}

## 체크리스트 항목 (총 ${checklists.length}개)
${checklistList}

위 체크리스트 각 항목에 대해 AI 가이드(hint)를 생성해 주세요.
반드시 체크리스트 순서대로 hints 배열에 담아주세요.
hints 배열의 길이는 체크리스트 항목 수(${checklists.length}개)와 정확히 일치해야 합니다.`;

  try {
    const response = await fetch(`${DIFY_HINT_CONFIG.baseUrl}/chat-messages`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${DIFY_HINT_CONFIG.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        inputs: {},
        query: prompt,
        response_mode: 'streaming',
        conversation_id: '',
        user: userId,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Dify API 오류: ${response.status} - ${errorText}`);
    }

    // SSE 스트림 파싱
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullAnswer = '';
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmedLine = line.trim();
          if (trimmedLine.startsWith('data: ')) {
            const data = trimmedLine.slice(6);
            if (data === '[DONE]') continue;

            try {
              const parsed = JSON.parse(data);
              if (parsed.event === 'agent_message' || parsed.event === 'message') {
                fullAnswer += parsed.answer || '';
              }
            } catch (e) {
              // JSON 파싱 실패는 무시
            }
          }
        }
      }

      // 버퍼에 남은 데이터 처리
      if (buffer.trim().startsWith('data: ')) {
        const data = buffer.trim().slice(6);
        if (data !== '[DONE]') {
          try {
            const parsed = JSON.parse(data);
            if (parsed.event === 'agent_message' || parsed.event === 'message') {
              fullAnswer += parsed.answer || '';
            }
          } catch (e) {
            // 무시
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    // JSON 파싱
    const parsedData = parseJsonResponse(fullAnswer);

    // hints 배열 추출 (다양한 응답 구조 대응)
    let hints = [];
    if (Array.isArray(parsedData.hints) && parsedData.hints.length > 0) {
      // 기대 형태: { hints: [...] }
      hints = parsedData.hints;
    } else if (Array.isArray(parsedData)) {
      // AI가 배열만 반환한 경우: [{ checklist_name, hint }, ...]
      hints = parsedData;
    } else {
      // 객체 내에서 첫 번째 배열 프로퍼티를 탐색
      const arrayProp = Object.values(parsedData || {}).find(
        (v) => Array.isArray(v) && v.length > 0
      );
      if (arrayProp) {
        hints = arrayProp;
      }
    }

    if (hints.length === 0) {
      console.warn(
        '[generateChecklistHints] AI 응답에서 hints 배열을 찾지 못했습니다. parsedData:',
        parsedData
      );
    }

    // hint 객체의 키 이름 정규화 (다양한 키 대응)
    hints = hints.map((h) => {
      if (!h || typeof h !== 'object') return { checklist_name: '', hint: '' };
      return {
        checklist_name: h.checklist_name || h.checklistName || h.name || h.title || '',
        hint: h.hint || h.guide || h.description || h.content || '',
      };
    });

    // 체크리스트-hint 매핑 (이름 기반 매칭 우선, 인덱스 폴백)
    return checklists.map((checklist, index) => {
      // 1. 이름 기반 정확 매칭 시도
      const nameMatch = hints.find(
        (h) => h.checklist_name && h.checklist_name.trim() === checklist.name.trim()
      );
      if (nameMatch) {
        return { checklistId: checklist.id, hint: nameMatch.hint || null };
      }

      // 2. 인덱스 기반 폴백
      if (hints[index]) {
        if (
          hints[index].checklist_name &&
          hints[index].checklist_name.trim() !== checklist.name.trim()
        ) {
          console.warn(
            `[generateChecklistHints] 이름 불일치 (index ${index}): 예상="${checklist.name}", 응답="${hints[index].checklist_name}"`
          );
        }
        return { checklistId: checklist.id, hint: hints[index].hint || null };
      }

      // 3. 매칭 실패
      console.warn(`[generateChecklistHints] hint 매칭 실패 (index ${index}): "${checklist.name}"`);
      return { checklistId: checklist.id, hint: null };
    });
  } catch (error) {
    console.error('[Dify Hint] AI 가이드 생성 오류:', error);
    throw error;
  }
};

// 스트리밍 중지 함수 export
export { stopChatMessage };

const difyApi = {
  generateProjectSchedule,
  validateProjectInput,
  askAiAssistant,
  recalculateCost,
  serializeProjectContext,
  stopChatMessage,
  generateChecklistHints,
};

export default difyApi;
